import type { ISpeedDataMarkResponse } from "@framework/types";

import type {
  IMultiAnalysisDataMark,
  IMultiAnalysisTimeRange,
  IStructureTreePoint,
} from "../../../multi-analysis/types";
import type { INyquistChartSample, INyquistChartSeries } from "./charts";
import { parseNyquistWallClock } from "./chart-utils";

export const AMPLITUDE_PHASE_MEASURE_TYPE = 4;
export const PHASE_UNIT_CODE = 133;

export interface INyquistControlParameter {
  title: string;
  units: string;
  value: number;
}

export interface INyquistDataMark extends IMultiAnalysisDataMark {
  nyquist: { controlParameter: INyquistControlParameter | null };
}

export const isNyquistDataMark = (mark: IMultiAnalysisDataMark): mark is INyquistDataMark => "nyquist" in mark;

export const mapNyquistDataMarks = (
  rows: readonly ISpeedDataMarkResponse[],
  points: readonly IStructureTreePoint[],
  formatDirection: (direction: number) => string,
  controlParameter: { title: string; units: string } | null,
): INyquistDataMark[] => {
  const pointsById = new Map(points.map(point => [point.id, point]));
  const marksByKey = new Map<string, INyquistDataMark>();
  const timesByKey = new Map<string, number>();

  for (const row of rows) {
    const point = pointsById.get(row.idPoint);
    if (!point || row.Type !== AMPLITUDE_PHASE_MEASURE_TYPE || !Number.isFinite(row.Value1)) continue;
    const timestamp = parseNyquistWallClock(row.Date);
    if (timestamp === null) continue;
    const key = `${row.idPoint}:${row.idMeasure}:${row.Date}`;
    const direction = Number.isFinite(point.direction) ? formatDirection(point.direction).trim() : "";
    const directionLabel = direction.startsWith("(") && direction.endsWith(")") ? direction : `(${direction})`;
    timesByKey.set(key, timestamp);
    marksByKey.set(key, {
      key,
      idMeasure: row.idMeasure,
      idPoint: row.idPoint,
      date: row.Date,
      measureDate: row.Date,
      pointName: direction ? `${point.title} ${directionLabel}` : point.title,
      measureName: row.Name,
      measureDescription: row.Description,
      value: row.Value1,
      value2: row.Value2 !== null && Number.isFinite(row.Value2) ? row.Value2 : null,
      units: row.Units,
      alarmStatus: row.AlarmStatus,
      rotationFrequencyRpm: row.RPM,
      param1: row.Param1,
      param2: row.Param2,
      nyquist: {
        controlParameter:
          controlParameter && row.ControlValue !== null && Number.isFinite(row.ControlValue)
            ? { ...controlParameter, value: row.ControlValue }
            : null,
      },
    });
  }

  return [...marksByKey.values()].sort(
    (left, right) => timesByKey.get(left.key)! - timesByKey.get(right.key)! || left.key.localeCompare(right.key),
  );
};

export const getNyquistFrameMarks = (
  marks: readonly IMultiAnalysisDataMark[],
  frameRange: IMultiAnalysisTimeRange,
): INyquistDataMark[] => {
  const parseBound = (value: IMultiAnalysisTimeRange["start"]) =>
    value ? parseNyquistWallClock(value instanceof Date ? value.toISOString() : value) : null;
  const start = parseBound(frameRange.start);
  const end = parseBound(frameRange.end);
  if (start === null || end === null) return [];
  const lower = Math.min(start, end);
  const upper = Math.max(start, end);
  return marks.filter(isNyquistDataMark).filter(mark => {
    const time = parseNyquistWallClock(mark.date);
    return time !== null && time >= lower && time <= upper;
  });
};

export const buildNyquistSeries = (
  marks: readonly INyquistDataMark[],
  getUnits: (unitCode: number) => string,
  getColor: (key: string) => string,
): INyquistChartSeries[] => {
  const series = new Map<string, Omit<INyquistChartSeries, "samples"> & { samples: INyquistChartSample[] }>();
  for (const mark of marks) {
    const key = `${mark.idPoint}:${mark.idMeasure}:${mark.units}`;
    const item = series.get(key) ?? {
      key,
      pointId: mark.idPoint,
      measureId: mark.idMeasure,
      name: `${mark.pointName} / ${mark.measureName}`,
      pointName: mark.pointName,
      measureName: mark.measureName,
      unitCode: mark.units,
      units: getUnits(mark.units),
      color: getColor(key),
      samples: [],
    };
    item.samples.push({
      key: mark.key,
      date: mark.date,
      amplitude: mark.value,
      phaseDegrees: mark.value2,
      rotationFrequencyRpm: mark.rotationFrequencyRpm,
    });
    series.set(key, item);
  }
  return [...series.values()].map(item => ({
    ...item,
    samples: item.samples
      .map(sample => ({ sample, time: parseNyquistWallClock(sample.date)! }))
      .sort((left, right) => left.time - right.time)
      .map(entry => entry.sample),
  }));
};

export const formatNyquistUnitLabel = (label: string): string => label.slice(label.lastIndexOf(",") + 1).trim();

export const groupNyquistSeriesByUnits = (series: readonly INyquistChartSeries[]): INyquistChartSeries[][] => {
  const groups = new Map<number, INyquistChartSeries[]>();
  for (const item of series) groups.set(item.unitCode, [...(groups.get(item.unitCode) ?? []), item]);
  return [...groups.values()];
};

export const resolveNyquistCursor = (
  marks: readonly INyquistDataMark[],
  cursorKey: string | null,
): INyquistDataMark | null => marks.find(mark => mark.key === cursorKey) ?? marks[marks.length - 1] ?? null;

export const formatNyquistDate = (date: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}:\d{2})/u.exec(date);
  return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}` : date;
};

export const formatNyquistValues = (
  mark: INyquistDataMark,
  amplitudeUnits: string,
  phaseUnits: string,
  formatValue: (value: number, unitCode: number | null) => string,
): string =>
  `${formatValue(mark.value, mark.units)} ${amplitudeUnits} / ${
    mark.value2 === null ? "—" : formatValue(mark.value2, PHASE_UNIT_CODE)
  } ${phaseUnits}`.trim();

export const formatNyquistHeader = (
  mark: INyquistDataMark,
  values: string,
  formatValue: (value: number, unitCode: number | null) => string,
): string => {
  const control = mark.nyquist.controlParameter;
  const parts = [mark.pointName, mark.measureName, formatNyquistDate(mark.date)];
  if (control) parts.push(`${formatValue(control.value, null)} ${control.units}`.trim());
  return `${parts.join(" / ")} : ${values}`;
};
